import { Roles } from '../../../../models/roles';
import { CashoutConfirmationDetail } from './cashout-currency.types';

/**
 * Convierte el payload final del cashout en filas legibles para el modal.
 * La confirmación se arma desde el mismo body que se enviará al backend para
 * evitar mostrar datos que no coincidan con la solicitud real.
 */
export function buildCashoutConfirmationDetails(
  ctx: any,
  body: any,
): CashoutConfirmationDetail[] {
  const transaction = Array.isArray(body) ? body[0] : body;
  const details = [...buildDynamicCashoutConfirmationDetails(ctx, transaction)];

  if (ctx.auth.hasRole(Roles.Admin)) {
    // Para admins, el cliente asignado debe verse junto al monto porque afecta
    // directamente a quién se le debita/acredita la operación.
    const insertIndex = details.findIndex((detail) =>
      detail.label.toLowerCase().startsWith('monto'),
    );
    details.splice(insertIndex >= 0 ? insertIndex + 1 : 1, 0, {
      label: 'Cliente asignado',
      value: getClientConfirmationLabel(ctx, transaction),
    });
  }

  return details
    .filter((detail) => hasConfirmationValue(detail.value))
    .map((detail) => ({
      ...detail,
      value: stringifyConfirmationValue(detail.value),
    }));
}

function buildDynamicCashoutConfirmationDetails(
  ctx: any,
  transaction: any,
): Array<{ label: string; value: any }> {
  const currency = (transaction?.currency ?? ctx.currency ?? '')
    .toString()
    .toUpperCase();
  const amount = formatConfirmationAmount(
    ctx,
    transaction?.amount,
    transaction?.currency,
  );
  const bank = formatConfirmationBank(transaction);
  const document = formatConfirmationDocument(transaction);
  const fullName = formatConfirmationFullName(transaction);
  const bitsoPayload = transaction?.bitso_mxn_payload ?? {};
  const fusePayload = transaction?.fuse_payout_payload ?? {};

  if (currency === 'MXN') {
    // MXN usa Bitso SPEI: la información bancaria vive dentro de bitso_mxn_payload.
    return [
      { label: 'ID de la transacción', value: transaction?.id },
      { label: 'Monto (MXN)', value: amount },
      { label: 'Tipo de transacción', value: transaction?.transaction_type },
      { label: 'Nombre(s) y apellidos beneficiario', value: fullName },
      { label: 'Protocolo SPEI', value: bitsoPayload?.protocol },
      {
        label: getBitsoMxnAccountLabel(bitsoPayload?.protocol),
        value: transaction?.account_number,
      },
      {
        label: 'Código de institución (banco)',
        value: bitsoPayload?.institution_code,
      },
      { label: 'RFC', value: bitsoPayload?.rfc },
      { label: 'Notas / concepto', value: bitsoPayload?.notes_ref },
    ];
  }

  if (currency === 'COP') {
    const companyId = String(transaction?.company_id ?? '').toUpperCase();
    const isBitso =
      companyId === 'BITSO_COP' || companyId.includes('BITSO_COP');
    const isNotbank =
      companyId.includes('NOTBANK') || !!transaction?.notbank_bank_account;
    const isBreb =
      !isBitso && !isNotbank && transaction?.minteo_channel === 'BREB';
    const keyData = transaction?.minteo_channel_data ?? {};
    const benBank =
      transaction?.bank_code ??
      transaction?.beneficiary_bank_code ??
      (isBitso ? bank : '');
    const nb = transaction?.notbank_bank_account;
    // COP: Bitso, Minteo, Notbank o banco directo.
    return [
      { label: 'ID de la transacción', value: transaction?.id },
      { label: 'Monto', value: amount },
      { label: 'Tipo de transacción', value: transaction?.transaction_type },
      {
        label: isBitso
          ? 'Proveedor de payout'
          : isNotbank
            ? 'Proveedor'
            : 'Canal de desembolso (Minteo)',
        value: isBitso
          ? 'Bitso COP (ACH)'
          : isNotbank
            ? 'Notbank COP'
            : transaction?.minteo_channel,
      },
      { label: 'Nombres y apellidos', value: fullName },
      { label: 'Documento de identidad', value: document },
      {
        label: isBreb
          ? 'Tipo de llave BreB'
          : isBitso
            ? 'Banco beneficiario'
            : isNotbank
              ? 'Banco Notbank'
              : 'Banco',
        value: isBreb
          ? keyData?.key_type
          : isBitso
            ? benBank
            : isNotbank
              ? nb?.bank || bank
              : bank,
      },
      {
        label: isBreb
          ? 'Valor de la llave'
          : isNotbank
            ? 'Número cuenta Notbank'
            : 'Número de cuenta',
        value: isNotbank
          ? nb?.number || transaction?.account_number
          : transaction?.account_number,
      },
      {
        label: 'Tipo de cuenta',
        value: isBreb ? '' : isNotbank ? nb?.kind || transaction?.account_type : transaction?.account_type,
      },
    ];
  }

  if (currency === 'AED' || currency === 'USD') {
    const beneficiary = fusePayload?.beneficiary ?? {};
    const address = beneficiary?.address ?? {};
    const accountIdentifier = beneficiary?.account_identifier ?? {};
    // AED/USD comparten proveedor Fuse/Lorum y pueden usar IBAN o wire.
    return [
      { label: 'ID de la transacción', value: transaction?.id },
      { label: `Monto (${currency})`, value: amount },
      { label: 'Tipo de transacción', value: transaction?.transaction_type },
      { label: 'Nombres y apellidos', value: fullName },
      { label: 'Número de cuenta', value: fusePayload?.account_id },
      { label: 'Descripción del pago', value: fusePayload?.purpose },
      { label: 'Referencia externa', value: fusePayload?.external_reference },
      { label: 'Tipo beneficiario', value: beneficiary?.type },
      {
        label: 'Medio de pago al beneficiario',
        value: accountIdentifier?.iban ? 'IBAN' : 'Wire',
      },
      { label: 'IBAN beneficiario', value: accountIdentifier?.iban },
      {
        label: 'Wire número de cuenta',
        value: accountIdentifier?.wire?.account_number,
      },
      {
        label: 'Wire routing',
        value: accountIdentifier?.wire?.routing_number,
      },
      { label: 'BIC banco beneficiario', value: beneficiary?.bank?.code },
      { label: 'País dirección', value: address?.country_code },
      { label: 'Dirección línea 1', value: address?.line_1 },
    ];
  }

  if (currency === 'BTC' || currency === 'ETH' || currency === 'USDT') {
    return [
      { label: 'ID de la transacción', value: transaction?.id },
      { label: `Monto (${currency})`, value: amount },
      { label: 'Tipo de transacción', value: transaction?.transaction_type },
      { label: 'Nombres y apellidos', value: fullName },
      { label: 'Red', value: transaction?.account_type },
      { label: 'Dirección destino', value: transaction?.account_number },
      { label: 'Descripción', value: transaction?.description },
    ];
  }

  if (['BRL', 'CLP', 'PEN', 'ARS'].includes(currency)) {
    const nb = transaction?.notbank_bank_account || {};
    return [
      { label: 'ID de la transacción', value: transaction?.id },
      { label: `Monto (${currency})`, value: amount },
      { label: 'Proveedor', value: bank || `Notbank ${currency}` },
      { label: 'Nombres y apellidos', value: fullName },
      { label: 'Banco Notbank', value: nb?.bank || bank },
      { label: 'Tipo de cuenta', value: nb?.kind || transaction?.account_type },
      {
        label: nb?.kind === 'br_pix' ? 'Clave PIX' : 'Número / clave',
        value: nb?.number || transaction?.account_number,
      },
      ...(currency === 'ARS'
        ? [
            { label: 'CBU', value: transaction?.cbu || nb?.cbu },
            { label: 'CUIT', value: transaction?.cuit || nb?.cuit },
            {
              label: 'Tipo persona',
              value: transaction?.person_type || nb?.person_type,
            },
          ]
        : []),
      ...(nb?.pix_type
        ? [{ label: 'Tipo PIX', value: nb.pix_type }]
        : []),
    ];
  }

  const defaultDetails = [
    { label: 'ID de la transacción', value: transaction?.id },
    { label: 'Monto', value: amount },
    { label: 'Tipo de transacción', value: transaction?.transaction_type },
    { label: 'Nombres y apellidos', value: fullName },
    { label: 'Tipo de cuenta', value: transaction?.account_type },
    { label: ctx.accountNumberLabel, value: transaction?.account_number },
    { label: 'Descripción', value: transaction?.description },
    { label: 'País dirección', value: transaction?.address_country },
    { label: 'Código postal', value: transaction?.address_zip },
    { label: 'Ciudad', value: transaction?.address_city },
    { label: 'Dirección', value: transaction?.address_street },
  ];

  if (currency !== 'EUR') {
    // EUR no muestra documento/banco en este bloque porque la información clave
    // del beneficiario se confirma con los campos de dirección ClearJunction.
    defaultDetails.splice(
      4,
      0,
      { label: 'Documento', value: document },
      { label: 'Banco', value: bank },
    );
  }

  return defaultDetails;
}

function getClientConfirmationLabel(ctx: any, transaction: any): string {
  const clientId = transaction?.client_id;
  const client = ctx.userService.users?.find(
    (item: any) => Number(item?.user_id ?? item?.id) === Number(clientId),
  );

  return client?.username ?? client?.email ?? clientId ?? '';
}

function formatConfirmationBank(transaction: any): string {
  const name = (transaction?.company_name ?? '').toString().trim();
  const code = (transaction?.company_id ?? '').toString().trim();
  return name && code ? `${name} - (${code})` : name || code;
}

function formatConfirmationDocument(transaction: any): string {
  const type = (transaction?.id_type ?? '').toString().trim();
  const number = (transaction?.id_number ?? '').toString().trim();
  return type && number ? `${type}-${number}` : type || number;
}

function formatConfirmationFullName(transaction: any): string {
  return [transaction?.firstname, transaction?.lastname]
    .map((value) => (value ?? '').toString().trim())
    .filter(Boolean)
    .join(' ');
}

function formatConfirmationAmount(
  ctx: any,
  amount: any,
  currency: any,
): string {
  if (!hasConfirmationValue(amount)) {
    return '';
  }

  const numeric = Number(amount);
  const formatted = Number.isFinite(numeric)
    ? numeric.toLocaleString('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      })
    : amount.toString();

  const symbol =
    ctx.currencyPicker?.shortname ??
    ctx.currencyPicker?.id ??
    currency ??
    ctx.currency;

  return `${symbol} ${formatted}`.trim();
}

// La confirmación MXN ya no depende del componente padre; calcula la etiqueta
// desde el protocolo Bitso incluido en el draft que se va a enviar.
function getBitsoMxnAccountLabel(protocol: any): string {
  const normalized = (protocol ?? '').toString().trim().toLowerCase();
  if (normalized === 'debitcard') return 'Número de tarjeta débito';
  if (normalized === 'phonenum') return 'Número móvil (10 dígitos típ.)';
  return 'CLABE (18 dígitos)';
}

function hasConfirmationValue(value: any): boolean {
  return value !== null && value !== undefined && value !== '';
}

function stringifyConfirmationValue(value: any): string {
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value.toString();
}
